import React, { useState } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  ShieldCheck,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// --- Types matching the AI pipeline output ---

export interface ScreeningKnockout {
  question: string;
  answer: string;
  impact: string;
  recommendation?: string;
}

export interface ScreeningConcern {
  question: string;
  answer: string;
  impact: string;
}

export interface AIScreeningData {
  knockouts: ScreeningKnockout[];
  concerns: ScreeningConcern[];
  clear: string[];
}

// --- Helper to extract screening data from rawResponse ---

/**
 * Safely extract screening analysis from a review's rawResponse field.
 * Handles both JSON string and object forms.
 */
export function extractScreeningAnalysis(
  rawResponse: Record<string, unknown> | string | null | undefined
): AIScreeningData | null {
  if (!rawResponse) return null;

  let parsed: Record<string, unknown>;
  if (typeof rawResponse === 'string') {
    try {
      parsed = JSON.parse(rawResponse);
    } catch {
      return null;
    }
  } else {
    parsed = rawResponse;
  }

  const sa = parsed.screeningAnalysis as AIScreeningData | null | undefined;
  if (!sa) return null;

  // Validate the structure has at least one of the expected arrays
  const knockouts = Array.isArray(sa.knockouts) ? sa.knockouts : [];
  const concerns = Array.isArray(sa.concerns) ? sa.concerns : [];
  const clear = Array.isArray(sa.clear) ? sa.clear : [];

  // If all arrays are empty, treat as no data
  if (knockouts.length === 0 && concerns.length === 0 && clear.length === 0) {
    return null;
  }

  return { knockouts, concerns, clear };
}

// --- Compact Badge for ReviewCard header ---

export interface AIScreeningBadgeProps {
  rawResponse: Record<string, unknown> | string | null | undefined;
  className?: string;
}

export const AIScreeningBadge: React.FC<AIScreeningBadgeProps> = ({
  rawResponse,
  className = '',
}) => {
  const screening = extractScreeningAnalysis(rawResponse);
  if (!screening) return null;

  const { knockouts, concerns, clear } = screening;

  if (knockouts.length > 0) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 ${className}`}
        title={`${knockouts.length} knockout screening issue${knockouts.length !== 1 ? 's' : ''}`}
      >
        <XCircle className="h-3 w-3" />
        Knockout{knockouts.length > 1 ? ` (${knockouts.length})` : ''}
      </span>
    );
  }

  if (concerns.length > 0) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ${className}`}
        title={`${concerns.length} screening concern${concerns.length !== 1 ? 's' : ''}`}
      >
        <AlertTriangle className="h-3 w-3" />
        Concern{concerns.length > 1 ? `s (${concerns.length})` : ''}
      </span>
    );
  }

  if (clear.length > 0) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 ${className}`}
        title="All screening questions cleared"
      >
        <CheckCircle className="h-3 w-3" />
        Screening Clear
      </span>
    );
  }

  return null;
};

AIScreeningBadge.displayName = 'AIScreeningBadge';

// --- Full Detail Section for ReviewDetail page ---

export interface AIScreeningDetailProps {
  rawResponse: Record<string, unknown> | string | null | undefined;
  className?: string;
  /** If true, section starts expanded. Defaults to true when knockouts exist. */
  defaultExpanded?: boolean;
}

export const AIScreeningDetail: React.FC<AIScreeningDetailProps> = ({
  rawResponse,
  className = '',
  defaultExpanded,
}) => {
  const screening = extractScreeningAnalysis(rawResponse);
  const hasIssues = screening
    ? screening.knockouts.length > 0 || screening.concerns.length > 0
    : false;

  const [isExpanded, setIsExpanded] = useState(
    defaultExpanded !== undefined ? defaultExpanded : hasIssues
  );

  if (!screening) return null;

  const { knockouts, concerns, clear } = screening;

  // Header styling
  const borderColorClass = knockouts.length > 0
    ? 'border-l-red-500 dark:border-l-red-400'
    : concerns.length > 0
      ? 'border-l-amber-500 dark:border-l-amber-400'
      : 'border-l-green-500 dark:border-l-green-400';

  const HeaderIcon = hasIssues ? ShieldAlert : ShieldCheck;
  const headerIconColor = knockouts.length > 0
    ? 'text-red-500 dark:text-red-400'
    : concerns.length > 0
      ? 'text-amber-500 dark:text-amber-400'
      : 'text-green-500 dark:text-green-400';

  const totalItems = knockouts.length + concerns.length + clear.length;

  return (
    <div
      className={`rounded-lg border border-gray-200 dark:border-gray-700 border-l-4 ${borderColorClass} bg-white dark:bg-gray-800 overflow-hidden ${className}`}
    >
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <HeaderIcon className={`h-5 w-5 ${headerIconColor}`} />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            AI Screening Analysis
          </h4>
          {/* Summary badges */}
          <div className="flex items-center gap-1.5 ml-1">
            {knockouts.length > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-medium">
                <XCircle className="h-3 w-3" />
                {knockouts.length}
              </span>
            )}
            {concerns.length > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium">
                <AlertCircle className="h-3 w-3" />
                {concerns.length}
              </span>
            )}
            {!hasIssues && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">
                <CheckCircle className="h-3 w-3" />
                All clear
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {totalItems} item{totalItems !== 1 ? 's' : ''}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Knockouts Section */}
          {knockouts.length > 0 && (
            <div className="px-4 py-3">
              <h5 className="flex items-center gap-1.5 text-sm font-semibold text-red-700 dark:text-red-400 mb-2">
                <XCircle className="h-4 w-4" />
                Knockouts ({knockouts.length})
              </h5>
              <div className="space-y-3">
                {knockouts.map((item, index) => (
                  <div
                    key={index}
                    className="rounded-lg bg-red-50/70 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3"
                  >
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {item.question}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Answer:</span>{' '}
                      {item.answer}
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 font-medium">
                      Impact: {item.impact}
                    </p>
                    {item.recommendation && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                        Recommendation: {item.recommendation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Concerns Section */}
          {concerns.length > 0 && (
            <div className={`px-4 py-3 ${knockouts.length > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''}`}>
              <h5 className="flex items-center gap-1.5 text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">
                <AlertCircle className="h-4 w-4" />
                Concerns ({concerns.length})
              </h5>
              <div className="space-y-3">
                {concerns.map((item, index) => (
                  <div
                    key={index}
                    className="rounded-lg bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 p-3"
                  >
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {item.question}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Answer:</span>{' '}
                      {item.answer}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 font-medium">
                      Impact: {item.impact}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clear Section */}
          {clear.length > 0 && (
            <div className={`px-4 py-3 ${(knockouts.length > 0 || concerns.length > 0) ? 'border-t border-gray-100 dark:border-gray-700' : ''}`}>
              <h5 className="flex items-center gap-1.5 text-sm font-semibold text-green-700 dark:text-green-400 mb-2">
                <CheckCircle className="h-4 w-4" />
                Clear ({clear.length})
              </h5>
              <ul className="space-y-1.5">
                {clear.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                  >
                    <CheckCircle className="h-3.5 w-3.5 text-green-400 dark:text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

AIScreeningDetail.displayName = 'AIScreeningDetail';
